import { styled } from '@mui/system';
import { Checkbox, CircularProgress, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';
import { paramRepository, ResourceTextApplication } from '../../../core/resources';
import { ADialog, ADialogActions, ADialogContent, ADialogTitle } from '../../../common/dialog';
import {
    IContract,
    IContractFarmData,
    IContractPerson,
    IProducerDetails,
} from '../../../interfaces/IContract';
import {
    ADateInput,
    ADateValue,
    ADropdown,
    ADropdownItemProps,
    ATextInput,
} from '../../../common/inputs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

import { ICompanyDetails, IContactPerson } from '../../../interfaces/IBusinessEntities';

import { useSnackbar } from 'notistack';
import {
    AliasTypeEnum,
    AnelmaContractStatusEnum,
    ContactPersonRoleTypeEnum,
} from '../../../interfaces/enums';
import FormValidator from '../../../core/FormValidator';
import { useAppSelector } from '../../../store/hooks';
import { se } from 'date-fns/locale';
import { isValid } from 'date-fns';
import { getProducerRoleForDifferentEnvironments } from './ProducerRoleHelper';
import { ACheckbox } from '../../../common/buttons/ACheckbox';

export interface Context {
    context: string;
    displayName: string;
    scopes: string[];
}

export interface IContractDialogProps {
    farm: ICompanyDetails;
    snellmanContactPersons: IContactPerson[];
    validator: FormValidator;
    saveContract: (contract: IContract) => void;
    onClose: () => any;
}

export default function ContractDialog(props: IContractDialogProps) {
    const { t } = useTranslation<ResourceTextApplication[]>(['AnelmaGeneral', 'AnelmaContract']);
    const { enqueueSnackbar } = useSnackbar();
    const { validator } = props;
    const configuration = useAppSelector((state) => state.configuration);

    const [loading, setLoading] = useState<boolean>(true);
    const isSavingRef = useRef<boolean>(false);

    const getDefaultFromDate = () => {
        let date = new Date();
        date.setHours(0, 0, 0, 0);

        return date;
    };
    const [contractTypeParameters, setContractTypeParameters] = useState<ADropdownItemProps[]>([]);
    const [contractNoticePeriodParameters, setContractNoticePeriodParameters] = useState<
        ADropdownItemProps[]
    >([]);
    const [snellmanContactPersonsDropDown, setSnellmanContactPersonsDropDown] = useState<
        ADropdownItemProps[]
    >([]);

    const [farmData, setFarmData] = useState<IContractFarmData | null>(null);
    const [fromDate, setFromDate] = useState<Date>(getDefaultFromDate());
    const [dueDate, setDueDate] = useState<Date | null>(null);
    const [noDueDate, setNoDueDate] = useState<boolean>(true);
    const [contractType, setContractType] = useState<string | number>('1');
    const [contractNoticePeriod, setContractNoticePeriod] = useState<string | number>('1');
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [selectedSnellmanContactPerson, setSelectedSnellmanContactPerson] =
        useState<IContactPerson>();
    const [selectedFarmContactPersons, setSelectedFarmContactPersons] = useState<string[]>([]);

    useEffect(() => {
        paramRepository.load(['AnelmaContract']).then(() => {
            let param = paramRepository.resource('AnelmaContract', 'ContractType');
            let param2 = paramRepository.resource('AnelmaContract', 'NoticePeriod');

            let dropdownItems: ADropdownItemProps[] = [];

            for (let i = 0; i < param.length; i++) {
                let item: ADropdownItemProps = {
                    text: param[i].text,
                    value: param[i].code,
                };

                dropdownItems.push(item);
            }

            let dropdownItems2: ADropdownItemProps[] = [];

            for (let i = 0; i < param2.length; i++) {
                let item: ADropdownItemProps = {
                    text: param2[i].text,
                    value: param2[i].code,
                };

                dropdownItems2.push(item);
            }

            setContractTypeParameters(dropdownItems);
            setContractNoticePeriodParameters(dropdownItems2);
        });

        setFarmData(getFarmData(props.farm));
        prepareSnellmanDropdown();
        setLoading(false);

        return () => {
            isSavingRef.current = false;
            setIsLoading(false);
        };
    }, []);

    // Reset loading state after a timeout to handle case where user cancels from next dialog
    useEffect(() => {
        if (isLoading) {
            const timer = setTimeout(() => {
                isSavingRef.current = false;
                setIsLoading(false);
            }, 5000);

            return () => clearTimeout(timer);
        }
    }, [isLoading]);

    const getFarmData = (farm: ICompanyDetails) => {
        let contactPersons: IContactPerson[] = [];
        for (let i = 0; i < farm.ContactPersons.length; i++) {
            if (
                farm.ContactPersons[i].Roles.find(
                    (role) => role.Name.search(getProducerRoleForDifferentEnvironments()) !== -1
                )
            ) {
                contactPersons.push(farm.ContactPersons[i]);
            }
        }

        let producerNumber = '';
        let producerNumberAlias = farm.Aliases.findIndex(
            (a) => a.Type === AliasTypeEnum.ProducerNumber
        );

        if (producerNumberAlias != -1) producerNumber = farm.Aliases[producerNumberAlias].Value;

        return {
            id: farm.Id,
            producerNumber: producerNumber,
            name: farm.Names[0].Value,
            adress: farm.Addresses[0].Street,
            postalCode: farm.Addresses[0].Zip,
            city: farm.Addresses[0].City,
            businessCode: farm.BusinessCode.Value,
            phone: farm.PhoneNumbers[0].NormalizedNumber,
            contactPersons: contactPersons,
        } as IContractFarmData;
    };

    const updateFromDate = (fromDate: ADateValue) => {
        if (fromDate) {
            fromDate.setHours(0, 0, 0, 0);
            setFromDate(fromDate);
        }
    };

    const updateDueDate = (dueDate: ADateValue) => {
        if (dueDate) {
            dueDate.setHours(0, 0, 0, 0);
            setDueDate(dueDate);
        }
    };

    const handleFarmPersonsSelection = (personId: string) => {
        const amountOfSelected = selectedFarmContactPersons.length;

        if (amountOfSelected >= 2 && !selectedFarmContactPersons.includes(personId)) {
            enqueueSnackbar(t('AnelmaContract:1049'), { variant: 'warning' });
            return;
        }

        if (selectedFarmContactPersons.includes(personId)) {
            setSelectedFarmContactPersons((previousState) => [
                ...previousState.filter((_, i) => _ !== personId),
            ]);
        } else {
            setSelectedFarmContactPersons((previousState) => [...previousState, personId]);
        }
    };

    const saveContract = useCallback(() => {
        if (isSavingRef.current) {
            return;
        }
        isSavingRef.current = true;

        flushSync(() => {
            setIsLoading(true);
        });

        if (valid() && selectedSnellmanContactPerson && farmData) {
            if (userConflict()) {
                enqueueSnackbar(t('AnelmaContract:1048'), {
                    variant: 'error',
                });
                isSavingRef.current = false;
                setIsLoading(false);
                return;
            }
            let snellmanUser: IContractPerson = {
                Id: selectedSnellmanContactPerson.PersonId,
                Name: selectedSnellmanContactPerson.Name,
                Address: selectedSnellmanContactPerson.Address,
                EmailAddress: selectedSnellmanContactPerson.EmailAddress,
            };

            let farmUsers: IContractPerson[] = [];

            for (let i = 0; i < selectedFarmContactPersons.length; i++) {
                let person = farmData.contactPersons.find(
                    (a) => a.PersonId === selectedFarmContactPersons[i]
                );
                if (person) {
                    let farmUser = {
                        Id: person.PersonId,
                        Name: person.Name,
                        Address: person.Address,
                        EmailAddress: person.EmailAddress,
                    };
                    farmUsers.push(farmUser);
                }
            }
            let producerDetails: IProducerDetails;

            if (props.farm) {
                producerDetails = {
                    ProducerNumber: farmData.producerNumber,
                    ProducerName: farmData.name,
                    ProducerAddress: farmData.adress,
                    ProducerPostalCode: farmData.postalCode,
                    ProducerCity: farmData.city,
                    ProducerBusinessId: farmData.businessCode,
                    ContractNoticePeriod: contractNoticePeriod.toString(),
                };

                var templates = Array.isArray(configuration.data.scrive)
                    ? configuration.data.scrive
                    : [];

                var templateObj = templates.find(
                    (template: any) => String(template.type) === String(contractType)
                );
                const templateId = templateObj?.templateId;

                if (!templateId) {
                    enqueueSnackbar(t('AnelmaGeneral:TemplateNotFound'), { variant: 'error' });
                    isSavingRef.current = false;
                    setIsLoading(false);
                    return;
                }

                let contract: IContract = {
                    Id: templateId,
                    AnimalType: '1',
                    Type: String(contractType), // '1',
                    ContractValidFrom: fromDate ?? new Date(),
                    ContractValidDue: dueDate,
                    FarmId: farmData.id || '',
                    Status: AnelmaContractStatusEnum.Draft,
                    SnellmanUser: snellmanUser,
                    FarmUser: farmUsers,
                    ProducerDetails: producerDetails,
                };
                props.saveContract(contract);
                // Don't reset loading state - let parent dialog handle closing/resetting
            }
        } else {
            isSavingRef.current = false;
            setIsLoading(false);

            enqueueSnackbar(t('AnelmaGeneral:1030'), {
                variant: 'error',
            });
        }
    }, [
        selectedSnellmanContactPerson,
        farmData,
        contractNoticePeriod,
        contractType,
        fromDate,
        dueDate,
        selectedFarmContactPersons,
        configuration.data.scrive,
        props,
        t,
        enqueueSnackbar,
        validator,
    ]);

    const isValidDateInput = (d: unknown): boolean => {
        if (d instanceof Date) return !isNaN(d.getTime());
        if (typeof d === 'string') return d.trim() !== '' && !isNaN(new Date(d).getTime());
        if (d === null) return false;
        return false;
    };

    const valid = () => {
        if (fromDate == null) {
            enqueueSnackbar(t('AnelmaGeneral:1148'), {
                variant: 'error',
            });
            return false;
        }

        if (dueDate == null && !noDueDate) {
            enqueueSnackbar(t('AnelmaGeneral:1148'), {
                variant: 'error',
            });
            return false;
        }

        if (dueDate !== null && !noDueDate) {
            const da = typeof dueDate === 'string' ? new Date(dueDate) : dueDate;
            if (!(da instanceof Date) || isNaN(da.getTime())) {
                enqueueSnackbar(t('AnelmaGeneral:1148'), {
                    variant: 'error',
                });
                return false;
            }
        }

        const d = typeof fromDate === 'string' ? new Date(fromDate) : fromDate;
        if (!(d instanceof Date) || isNaN(d.getTime())) {
            enqueueSnackbar(t('AnelmaGeneral:1148'), {
                variant: 'error',
            });
            return false;
        }

        if (!selectedSnellmanContactPerson) {
            return false;
        }

        if (!Array.isArray(selectedFarmContactPersons) || selectedFarmContactPersons.length === 0) {
            return false;
        }

        if (!validator?.valid) {
            return false;
        }

        return true;
    };

    const userConflict = () => {
        if (!selectedSnellmanContactPerson) return false;
        if (!farmData) return false;
        return selectedFarmContactPersons.find((i) => i === selectedSnellmanContactPerson.PersonId);
    };

    const prepareSnellmanDropdown = () => {
        let dropdownArray: ADropdownItemProps[] = [];
        for (let i = 0; i < props.snellmanContactPersons.length; i++) {
            const item = {
                text: props.snellmanContactPersons[i].Name,
                value: props.snellmanContactPersons[i].Id,
            };

            dropdownArray.push(item);
        }

        setSnellmanContactPersonsDropDown(dropdownArray);
    };

    const changeRequiredDueDate = () => {
        if (!noDueDate) {
            setDueDate(null);
        } else {
            setDueDate(new Date('invalid'));
        }

        setNoDueDate(!noDueDate);
    };

    return (
        <>
            <ADialog onClose={() => {}} open={true}>
                <ADialogContent size='md'>
                    <ADialogTitle>{t('AnelmaContract:1002')}</ADialogTitle>

                    {loading || isLoading ? (
                        <Grid style={{ textAlign: 'center' }}>
                            <CircularProgress />
                        </Grid>
                    ) : (
                        <>
                            <Grid style={{ paddingLeft: '24px', paddingBottom: '16px' }}>
                                <Typography variant='h5'>{t('AnelmaContract:1014')}</Typography>
                            </Grid>
                            <ADropdown
                                label={t('AnelmaContract:1015')}
                                id='contract-type-dropdown'
                                items={contractTypeParameters}
                                onChange={(v) => {
                                    if (v !== null) setContractType(Number(v));
                                }}
                                value={contractType}
                                validator={validator}
                                required
                            ></ADropdown>

                            <Grid style={{ paddingLeft: '24px', paddingBottom: '16px' }}>
                                <Typography variant='h5'>{t('AnelmaContract:1016')}</Typography>
                            </Grid>
                            <Grid style={{ display: 'flex', flexDirection: 'row' }}>
                                <ADateInput
                                    id='from-date'
                                    label={t('AnelmaContract:1017')}
                                    onChange={(v) => updateFromDate(v)}
                                    required
                                    error={!isValidDateInput(fromDate)}
                                    validator={validator}
                                    value={fromDate}
                                    maxDate={dueDate ?? undefined}
                                />

                                <ADateInput
                                    id='due-date'
                                    label={t('AnelmaContract:1018')}
                                    onChange={(v) => updateDueDate(v)}
                                    disabled={noDueDate}
                                    validator={validator}
                                    value={dueDate}
                                    minDate={fromDate ?? undefined}
                                />

                                <ACheckbox
                                    name='farmContact'
                                    value={noDueDate}
                                    checked={noDueDate}
                                    onChange={changeRequiredDueDate}
                                />
                                <Typography variant='body1'>{t('AnelmaContract:1039')}</Typography>
                            </Grid>
                            <Grid style={{ paddingLeft: '24px', paddingBottom: '16px' }}>
                                <Typography variant='h5'>{t('AnelmaContract:1038')}</Typography>
                            </Grid>
                            <ADropdown
                                label={t('AnelmaContract:1038')}
                                id='termination-options-dropdown'
                                items={contractNoticePeriodParameters}
                                onChange={(v) => {
                                    if (v !== null) setContractNoticePeriod(v.toString());
                                }}
                                value={contractNoticePeriod}
                                validator={validator}
                                required
                            />
                            <Grid style={{ paddingLeft: '24px', paddingBottom: '16px' }}>
                                <Typography variant='h5'>{t('AnelmaContract:1019')}</Typography>
                            </Grid>
                            <Grid style={{ paddingLeft: '24px', paddingBottom: '16px' }}>
                                <Typography variant='h6'>{t('AnelmaContract:1022')}</Typography>
                            </Grid>
                            <ADropdown
                                label={t('AnelmaContract:1020')}
                                id='snellman-person-dropdown'
                                items={snellmanContactPersonsDropDown}
                                onChange={(v) => {
                                    if (v !== null) {
                                        setSelectedSnellmanContactPerson(
                                            props.snellmanContactPersons.find((i) => i.Id === v)
                                        );
                                    }
                                }}
                                value={selectedSnellmanContactPerson?.Id || ''}
                                validator={validator}
                                required
                            />

                            {selectedSnellmanContactPerson && (
                                <Grid container>
                                    <Grid size={{ sm: 6 }}>
                                        <ATextInput
                                            disabled
                                            id='name'
                                            label={t('AnelmaContract:1023')}
                                            maxLength={64}
                                            minLength={2}
                                            onChange={(v) => {}}
                                            required
                                            validator={validator}
                                            value={selectedSnellmanContactPerson.Name}
                                        />
                                    </Grid>

                                    <Grid size={{ sm: 6 }}>
                                        <ATextInput
                                            disabled
                                            id='email'
                                            label={t('AnelmaGeneral:1013')}
                                            maxLength={64}
                                            minLength={2}
                                            onChange={(v) => {}}
                                            required
                                            validator={validator}
                                            value={selectedSnellmanContactPerson.EmailAddress}
                                        />
                                    </Grid>

                                    <Grid size={{ sm: 6 }}>
                                        <ATextInput
                                            disabled
                                            id='address'
                                            label={t('AnelmaContract:1024')}
                                            maxLength={64}
                                            minLength={2}
                                            onChange={(v) => {}}
                                            required
                                            validator={validator}
                                            value={selectedSnellmanContactPerson.Address}
                                        />
                                    </Grid>

                                    <Grid size={{ sm: 6 }}>
                                        <ATextInput
                                            disabled
                                            id='phone'
                                            label={t('AnelmaContract:1025')}
                                            maxLength={64}
                                            minLength={2}
                                            onChange={(v) => {}}
                                            required
                                            validator={validator}
                                            value={selectedSnellmanContactPerson.PhoneNumber}
                                        />
                                    </Grid>
                                </Grid>
                            )}

                            {farmData && (
                                <>
                                    <Grid style={{ paddingLeft: '24px', paddingBottom: '16px' }}>
                                        <Typography variant='h6'>{farmData.name}</Typography>
                                    </Grid>

                                    <Grid container>
                                        <Grid size={{ sm: 6 }}>
                                            <ATextInput
                                                disabled
                                                id='businesscode'
                                                label={t('AnelmaContract:1026')}
                                                onChange={() => {}}
                                                required
                                                validator={validator}
                                                value={farmData.businessCode}
                                            />
                                        </Grid>

                                        <Grid size={{ sm: 6 }}>
                                            <ATextInput
                                                disabled
                                                id='company-producer-number'
                                                label={t('AnelmaGeneral:1064')}
                                                onChange={() => {}}
                                                required
                                                validator={validator}
                                                value={farmData.producerNumber}
                                            />
                                        </Grid>
                                    </Grid>

                                    <Grid container>
                                        <Grid size={{ sm: 6 }}>
                                            <ATextInput
                                                disabled
                                                id='company-address'
                                                label={t('AnelmaContract:1024')}
                                                onChange={() => {}}
                                                required
                                                validator={validator}
                                                value={farmData.adress}
                                            />
                                        </Grid>

                                        <Grid size={{ sm: 6 }}>
                                            <ATextInput
                                                disabled
                                                id='company-address-zip-and-city'
                                                label={''}
                                                onChange={() => {}}
                                                required
                                                validator={validator}
                                                value={farmData.postalCode + ' ' + farmData.city}
                                                style={{ marginTop: '16px' }}
                                            />
                                        </Grid>
                                    </Grid>

                                    <div style={{ display: 'flex', flexFlow: 'wrap' }}>
                                        {farmData.contactPersons.map((person) => (
                                            <label
                                                key={person.PersonId}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    marginBottom: '8px',
                                                }}
                                            >
                                                <ACheckbox
                                                    name='farmContact'
                                                    value={person.Name}
                                                    checked={selectedFarmContactPersons.includes(
                                                        person.PersonId
                                                    )}
                                                    onChange={() =>
                                                        handleFarmPersonsSelection(person.PersonId)
                                                    }
                                                />
                                                <Typography variant='body1'>{`${t(
                                                    'AnelmaContract:1003'
                                                )} ${person.Name}`}</Typography>
                                            </label>
                                        ))}
                                    </div>

                                    {selectedFarmContactPersons.length > 0 &&
                                        selectedFarmContactPersons.map((p) => {
                                            let i = farmData.contactPersons.findIndex(
                                                (c) => c.PersonId === p
                                            );
                                            let name =
                                                i !== -1 ? farmData.contactPersons[i].Name : '';
                                            let email =
                                                i !== -1
                                                    ? farmData.contactPersons[i].EmailAddress
                                                    : '';

                                            return (
                                                <Grid container>
                                                    <Grid size={{ sm: 6 }}>
                                                        <ATextInput
                                                            disabled
                                                            id='email'
                                                            label={
                                                                t('AnelmaGeneral:1013') + ' ' + name
                                                            }
                                                            maxLength={64}
                                                            minLength={2}
                                                            onChange={(v) => {}}
                                                            required
                                                            validator={validator}
                                                            value={email}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            );
                                        })}
                                </>
                            )}

                            <ADialogActions
                                buttons={{
                                    left: [
                                        {
                                            onClick: () => props.onClose(),
                                            type: 'cancel',
                                        },
                                    ],
                                    right: [
                                        {
                                            onClick: saveContract,
                                            type: 'save',
                                            disabled: isLoading,
                                        },
                                    ],
                                }}
                            ></ADialogActions>
                        </>
                    )}
                </ADialogContent>
            </ADialog>
        </>
    );
}
